import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

/**
 * The footer.
 *
 * Carries the three legal documents a visitor is entitled to reach from any page,
 * the data-provenance notice section 2.1 requires, and the status page link.
 * The opt-out route for the public company pages is referenced here too, so a
 * company that finds itself indexed has a visible way out (section 23.8).
 */
export function Footer(): React.ReactNode {
  const { t } = useTranslation();
  const year = new Date().getFullYear();

  return (
    <footer className="mt-16 border-t border-[var(--color-border)] bg-[var(--color-surface-2)]">
      <div className="mx-auto max-w-6xl px-4 py-10">
        <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-4">
          <div>
            <div className="text-lg font-extrabold text-[var(--color-fg)]">FOCO</div>
            <p className="mt-2 text-sm text-[var(--color-muted-fg)]">{t('footer.tagline')}</p>
          </div>

          <nav aria-label={t('footer.product')}>
            <h2 className="text-xs font-semibold uppercase tracking-wide text-[var(--color-muted-fg)]">
              {t('footer.product')}
            </h2>
            <ul className="mt-3 space-y-2 text-sm">
              <li>
                <Link className="text-[var(--color-fg)] hover:text-brand-600" to="/pricing">
                  {t('footer.pricing')}
                </Link>
              </li>
              <li>
                <Link className="text-[var(--color-fg)] hover:text-brand-600" to="/docs/api">
                  {t('footer.apiDocs')}
                </Link>
              </li>
              <li>
                <Link className="text-[var(--color-fg)] hover:text-brand-600" to="/status">
                  {t('footer.status')}
                </Link>
              </li>
            </ul>
          </nav>

          <nav aria-label={t('footer.legal')}>
            <h2 className="text-xs font-semibold uppercase tracking-wide text-[var(--color-muted-fg)]">
              {t('footer.legal')}
            </h2>
            <ul className="mt-3 space-y-2 text-sm">
              <li>
                <Link className="text-[var(--color-fg)] hover:text-brand-600" to="/legal/eula">
                  {t('footer.eula')}
                </Link>
              </li>
              <li>
                <Link className="text-[var(--color-fg)] hover:text-brand-600" to="/legal/privacy">
                  {t('footer.privacy')}
                </Link>
              </li>
              <li>
                <Link className="text-[var(--color-fg)] hover:text-brand-600" to="/legal/cookies">
                  {t('footer.cookies')}
                </Link>
              </li>
              {/* Required by section 23.8: a company listed on a public page
                  must be able to ask to be removed, from a findable place. */}
              <li>
                <Link className="text-[var(--color-fg)] hover:text-brand-600" to="/opt-out">
                  {t('publicPage.optOutLink', 'Remove a company from public view')}
                </Link>
              </li>
            </ul>
          </nav>

          <div>
            <h2 className="text-xs font-semibold uppercase tracking-wide text-[var(--color-muted-fg)]">
              {t('footer.company')}
            </h2>
            <p className="mt-3 text-sm text-[var(--color-muted-fg)]">
              <a className="hover:text-brand-600" href="mailto:support@foco.arriba.digital">
                support@foco.arriba.digital
              </a>
            </p>
          </div>
        </div>

        <div className="mt-8 border-t border-[var(--color-border)] pt-6 text-xs text-[var(--color-muted-fg)]">
          <p>{t('footer.copyright', { year })}</p>
          <p className="mt-1">{t('footer.dataNotice')}</p>
        </div>
      </div>
    </footer>
  );
}
